import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { EMultipleAuthViewMode } from '../core/security-authentication.enum';
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';
import { DomainAuthenticator, MultipleAuthenticator } from '../core/authentication-contracts';

@Component({
  selector: 'biz-multiple-auth-detail',
  templateUrl: './multiple-auth-detail.component.html',
  styleUrls: ['./multiple-auth-detail.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state(
        'void',
        style({
          opacity: 0
        })
      ),
      transition('void <=> *', animate(500))
    ])
  ]
})
export class MultipleAuthDetailComponent implements OnInit {
  private _selectedAuthenticator: MultipleAuthenticator;
  get selectedAuthenticator() {
    return this._selectedAuthenticator;
  }
  @Input() set selectedAuthenticator(value: MultipleAuthenticator) {

    if (
      this.viewMode === EMultipleAuthViewMode.Edit &&
      this.selectedAuthenticator &&
      value &&
      this.selectedAuthenticator.displayName === value.displayName
    ) {
      this._selectedAuthenticator = _.cloneDeep({
        ...value,
        displayName: this._selectedAuthenticator.displayName || '',
        type: this._selectedAuthenticator.type || '',
        subtype: this._selectedAuthenticator.subtype
          ? this._selectedAuthenticator.subtype
          : '',
        domains:
          value.domains.length === 0
            ? this.selectedAuthenticator.domains || value.domains
            : value.domains,
        oauthOptions: this._selectedAuthenticator.oauthOptions
          ? this._selectedAuthenticator.oauthOptions
          : {}
      });
      if (this._selectedAuthenticator.type === 'OAuth2') {
        this._selectedAuthenticator.oauthOptions =
          this._selectedAuthenticator.oauthOptions || value.oauthOptions;
      } else {
        this._selectedAuthenticator.options =
          this._selectedAuthenticator.options || value.options;
      }
    } else if (
      this.viewMode === EMultipleAuthViewMode.Add &&
      this._selectedAuthenticator
    ) {
      value = value || {
        displayName: '',
        type: '',
        subtype: '',
        enabled: true,
        domains: []
      };

      this._selectedAuthenticator = _.cloneDeep({
        ...value,
        displayName: this._selectedAuthenticator.displayName || '',
        type: this._selectedAuthenticator.type || '',
        subtype: this._selectedAuthenticator.subtype
          ? this._selectedAuthenticator.subtype
          : '',
        domains:
          value.domains.length === 0
            ? this.selectedAuthenticator.domains || value.domains
            : value.domains,
        oauthOptions: this._selectedAuthenticator.oauthOptions
          ? this._selectedAuthenticator.oauthOptions
          : {}
      });
      if (this._selectedAuthenticator.type === 'OAuth2') {
        this._selectedAuthenticator.oauthOptions =
          this._selectedAuthenticator.oauthOptions || value.oauthOptions;
      } else {
        this._selectedAuthenticator.options =
          this._selectedAuthenticator.options || value.options;
      }
    } else {
      this._selectedAuthenticator = _.cloneDeep(value);
    }

    this._originalAuthenticator = _.cloneDeep(this._selectedAuthenticator);
    if (value) {
      this.showAddDomain = !this._selectedAuthenticator.domains.length;
      this.domainList = [...this._selectedAuthenticator.domains];
    }
  }

  private _originalAuthenticator: MultipleAuthenticator;
  public domainList: DomainAuthenticator[];

  @Input() viewMode: EMultipleAuthViewMode;
  @Input() isEditable = true;
  @Input() isDisabled = false;
  @Input() metadata: Array<any>;
  @Input() optionsDefaultValues: any;

  @Output() onSetNewAuthenticator = new EventEmitter<MultipleAuthenticator>();
  @Output() onUpdateAuthenticator = new EventEmitter<MultipleAuthenticator>();
  @Output() onCancel = new EventEmitter<any>();
  @Output() onChangeDomainList = new EventEmitter<DomainAuthenticator[]>();

  showAddDomain = false;
  newDomainValue = '';
  newDomainDisplayName = '';
  authenticatorDisplayName = '';
  uniqueByTypeArray: Array<any>;
  typeFilteredMetadataArray: Array<any>;
  availableFlows: Array<any> = [];
  editModeDomain: any;

  private _invalidDomainNameChars = ' \\,;@¨`´^~°¬ÃÀÁÄÂÈÉËÊÌÍÏÎÒÓÖÔÙÚÜÛãàáäâèéëêìíïîòóöôùúüûÑñÇç';

  constructor() { }

  ngOnInit() {

  }

  ngOnChanges(): void {
    if (!this.isDisabled) {
      this.uniqueByTypeArray = _.uniqBy(_.filter(this.metadata, (m) => m.enabled), 'type');
    } else {
      this.uniqueByTypeArray = _.uniqBy(this.metadata, 'type');
    }
  }

  onChangeAuthenticatorType(authenticatorType: string) {
    if (this._selectedAuthenticator.type !== authenticatorType) {
      this._selectedAuthenticator.type = authenticatorType;
      this._selectedAuthenticator.subtype = '';

      if (authenticatorType === 'OAuth2') {
        delete this._selectedAuthenticator.options;
        if (!this._selectedAuthenticator.oauthOptions) {
          this._selectedAuthenticator.oauthOptions = {};
          this._selectedAuthenticator.oauthOptions['activeFlow'] = '';
        }
      } else {
        delete this._selectedAuthenticator.oauthOptions;
        this.availableFlows = [];
      }
    }

    this.typeFilteredMetadataArray = _.filter(this.metadata, function (item) {
      return item.type === authenticatorType && item.subtype !== '';
    });
  }

  onChangeAuthenticatorSubtype(authenticatorSubtype: string) {
    const self = this;

    if (this._selectedAuthenticator.subtype !== authenticatorSubtype) {
      this._selectedAuthenticator.subtype = authenticatorSubtype;
      if (this.selectedAuthenticator.type === 'OAuth2') {
        this.selectedAuthenticator.oauthOptions.activeFlow = '';
      }
    }

    if (this.selectedAuthenticator.type === 'OAuth2') {
      const subtypeFilteredMetadata = _.find(this.metadata, function (item) {
        return item.type === self._selectedAuthenticator.type && item.subtype === authenticatorSubtype;
      })
      this.availableFlows = subtypeFilteredMetadata ? subtypeFilteredMetadata.template.flows : [];
    }
  }

  onChangeAuthenticatorFlow(authenticatorFlow) {
    this._selectedAuthenticator.oauthOptions.activeFlow = authenticatorFlow;
  }

  domainTableAction(action: string) {
    switch (action) {
      case 'enable':
        this.showAddDomain = true;
        break;
      case 'disable':
        this.showAddDomain = false;
        this.newDomainValue = '';
        this.newDomainDisplayName = '';
        break;
      case 'add':
        if (!!this.newDomainValue && !!this.newDomainDisplayName) {
          this.domainList.push({
            name: this.newDomainValue,
            displayName: this.newDomainDisplayName
          });
          this.newDomainValue = '';
          this.newDomainDisplayName = '';
          this.showAddDomain = false;
        }
        this.onChangeDomainList.emit(this.domainList);
        break;
    }
  }

  private _setNewOptionsToSelectedAuthenticator() {
    if (this._selectedAuthenticator.type === 'OAuth2') {
      delete this._selectedAuthenticator.options;

      if (this._selectedAuthenticator.oauthOptions &&
        this._selectedAuthenticator.subtype && this._selectedAuthenticator.subtype !== '' &&
        this._selectedAuthenticator.oauthOptions.activeFlow && this._selectedAuthenticator.oauthOptions.activeFlow !== '') {

        const selectedMetadata = _.find(this.metadata, (element) =>
          element.type === this._selectedAuthenticator.type && element.subtype === this._selectedAuthenticator.subtype);

        this._selectedAuthenticator.oauthOptions['activeFlow'] = this._selectedAuthenticator.oauthOptions.activeFlow;

        const activeFlowMetadata = _.find(selectedMetadata.template.flows, (flow) => flow.name === this._selectedAuthenticator.oauthOptions['activeFlow']);
        const defaultValues = this.optionsDefaultValues[
          this._selectedAuthenticator.type
        ][this._selectedAuthenticator.subtype][
          this._selectedAuthenticator.oauthOptions['activeFlow']
        ];

        let activeFlowMetadataCopy = JSON.parse(JSON.stringify(activeFlowMetadata));

        activeFlowMetadataCopy.properties = defaultValues;

        this._selectedAuthenticator.oauthOptions['flows'] = [];
        this._selectedAuthenticator.oauthOptions['flows'].push(activeFlowMetadataCopy);
      }
    } else {
      delete this._selectedAuthenticator.oauthOptions;
      this._selectedAuthenticator.options = {};
      if (
        this._selectedAuthenticator.type &&
        this._selectedAuthenticator.type !== '' &&
        this._selectedAuthenticator.subtype &&
        this._selectedAuthenticator.subtype !== ''
      ) {
        const defaultValues = this.optionsDefaultValues[
          this._selectedAuthenticator.type
        ][this._selectedAuthenticator.subtype];
        this._selectedAuthenticator.options = defaultValues;
      } else if (
        this._selectedAuthenticator.type &&
        this._selectedAuthenticator.type !== ''
      ) {
        const defaultValues = this.optionsDefaultValues[
          this._selectedAuthenticator.type
        ];
        this._selectedAuthenticator.options = defaultValues;
      }
    }
  }

  setNewAuthenticator() {
    this._setNewOptionsToSelectedAuthenticator();

    if (!this._originalAuthenticator.options && !this._originalAuthenticator.oauthOptions) {
      if (this._selectedAuthenticator.options) {
        this._originalAuthenticator.options = this.selectedAuthenticator.options;
      } else if (this._selectedAuthenticator.oauthOptions) {
        this._originalAuthenticator.oauthOptions = this.selectedAuthenticator.oauthOptions;
      }
    }

    this.onSetNewAuthenticator.emit(this._selectedAuthenticator);
  }

  updateAuthenticatorOptions() {
    if (
      this._originalAuthenticator.type !== this._selectedAuthenticator.type ||
      (this._originalAuthenticator.type === this._selectedAuthenticator.type &&
        this._originalAuthenticator.subtype !==
        this._selectedAuthenticator.subtype) ||
      (this._originalAuthenticator.type === 'OAuth2' &&
        this._selectedAuthenticator.type === 'OAuth2' &&
        this._originalAuthenticator.oauthOptions.activeFlow !==
        this._selectedAuthenticator.oauthOptions.activeFlow)
    ) {
      this._setNewOptionsToSelectedAuthenticator();
    }

    this._originalAuthenticator = _.cloneDeep(this._selectedAuthenticator);
  }

  updateAuthenticator() {
    this.onUpdateAuthenticator.emit(this._selectedAuthenticator);
  }

  get hasChanges(): boolean {
    return !_.isEqual(this._originalAuthenticator, this._selectedAuthenticator);
  }


  deleteDomain(domainToRemove) {
    _.remove(
      this._selectedAuthenticator.domains,
      domain => domain === domainToRemove
    );
    this.onChangeDomainList.emit(this.domainList);
  }

  cancel() {
    this.onCancel.emit();
  }

  isFormValid() {
    if (!this.selectedAuthenticator) {
      return true;
    }

    let isTypeValid = true;
    let isSubtypeValid = true;
    let isFlowValid = true;
    const isDisplayNameValid = !!this.selectedAuthenticator.displayName &&
      this.selectedAuthenticator.displayName !== '' &&
      this.selectedAuthenticator.displayName.trim() !== '';

    if (this.uniqueByTypeArray && this.uniqueByTypeArray.length > 0) {
      isTypeValid = this.selectedAuthenticator.type !== '';
    }

    if (this.typeFilteredMetadataArray && this.typeFilteredMetadataArray.length > 0) {
      isSubtypeValid = this.selectedAuthenticator.subtype !== '';
    }

    if (this.availableFlows && this.availableFlows.length > 0 && this.selectedAuthenticator.oauthOptions) {
      isFlowValid = this.selectedAuthenticator.oauthOptions.activeFlow !== '';
    }

    return isDisplayNameValid && isTypeValid && isSubtypeValid && isFlowValid;

  }

  validateDomainNamePaste(event) {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const inputChar = clipboardData.getData('text');

    _.forEach(inputChar, (char) => {
      if (this._invalidDomainNameChars.indexOf(char) !== -1) {
        // invalid character, prevent input
        event.preventDefault();
        return false;
      }
    })
  }

  validateDomainNameKey(event) {
    const inputChar = String.fromCharCode(event.charCode);

    if (this._invalidDomainNameChars.indexOf(inputChar) !== -1) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }
}
